import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
} from 'lucide-react';
import { GUIDE_STEPS } from '../../utils/boothGuide';
import { songToText } from '../../utils/boothSongs';
import SendToArranger from './SendToArranger';

const HOOK_STARTERS = [
  'What do you want people to feel when this plays?',
  "What's the one thing you'd say if you only had 10 seconds?",
  "Finish this: 'They don't know...'",
];

const VERSE_STARTERS = [
  'Where are you when this story starts?',
  'What happened that changed things?',
  'What did you learn that you\'d tell your younger self?',
];

const copySong = async (song) => {
  const text = songToText(song);
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_error) {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    area.remove();
    return copied;
  }
};

const BeatCounter = ({ color, playing }) => (
  <div className="guide-counter-wrap rounded-2xl border border-white/10 bg-black/20 p-4">
    <div
      className={`guide-beat-counter grid grid-cols-4 gap-2 ${playing ? 'is-playing' : ''}`}
      aria-label={`Two bar beat counter, ${playing ? 'playing' : 'stopped'}`}
    >
      {Array.from({ length: 8 }).map((_, index) => (
        <span
          key={index}
          className="guide-beat-light"
          style={{ '--light': color, '--light-index': index }}
        />
      ))}
    </div>
    <p className="mt-3 text-base font-bold text-white/65">
      One bar = 4 beats. One line of lyrics usually fills one bar.
    </p>
  </div>
);

export const LocalStarters = ({ step }) => {
  const starters = step === 3 ? HOOK_STARTERS : VERSE_STARTERS;
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
      <p className="font-heading text-lg font-black">If you're stuck, start here</p>
      <ul className="mt-2 space-y-2 text-base font-bold leading-relaxed text-white/70">
        {starters.map((starter) => <li key={starter}>{starter}</li>)}
      </ul>
      <p className="mt-3 text-sm font-bold uppercase tracking-[0.12em] text-white/40">
        Local prompts. No AI.
      </p>
    </div>
  );
};

const FinishCard = ({ color, onDownload, onKeepWriting, song }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const worked = await copySong(song);
    setCopied(worked);
  };

  return (
    <div className="guide-finish relative mt-5 grid gap-3 overflow-hidden sm:grid-cols-3">
      <div className="guide-finish-burst" aria-hidden="true" style={{ '--burst': color }} />
      <article className="relative rounded-2xl border border-white/10 bg-black/25 p-4">
        <h3 className="font-heading text-xl font-black">Record it</h3>
        <p className="mt-2 text-base leading-relaxed text-white/65">
          Play the beat out loud from this computer or a speaker, and record your voice on your
          phone's voice memo app. Read your flow notes first.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="flex min-h-11 items-center gap-2 rounded-full border border-white/15
              px-3 text-base font-black"
          >
            <Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy everything'}
          </button>
          <button
            type="button"
            onClick={onDownload}
            className="flex min-h-11 items-center gap-2 rounded-full border border-white/15
              px-3 text-base font-black"
          >
            <Download className="h-4 w-4" /> Download .txt
          </button>
        </div>
      </article>
      <article className="relative rounded-2xl border border-white/10 bg-black/25 p-4">
        <h3 className="font-heading text-xl font-black">Take it further</h3>
        <p className="mt-2 text-base leading-relaxed text-white/65">
          Send your beat and lyrics to Beat Arranger to build the full track, or paste your lyrics
          into Soundtrap.
        </p>
        <SendToArranger song={song} className="mt-4" />
      </article>
      <article className="relative rounded-2xl border border-white/10 bg-black/25 p-4">
        <h3 className="font-heading text-xl font-black">Keep writing</h3>
        <p className="mt-2 text-base leading-relaxed text-white/65">
          Add a second verse and an outro, then keep building on what you started.
        </p>
        <button
          type="button"
          onClick={onKeepWriting}
          className="neon-btn mt-4 min-h-11 rounded-full px-4 text-base font-black text-black"
          style={{ backgroundColor: color }}
        >
          Add Verse 2 and Outro
        </button>
      </article>
    </div>
  );
};

const GuidePanel = ({
  aiAvailable,
  bpm,
  color,
  complete,
  onBack,
  onDownload,
  onExit,
  onFlowConfirmed,
  onKeepWriting,
  onNext,
  onNoSound,
  onSkip,
  playing,
  song,
  step,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const detail = GUIDE_STEPS[step] || GUIDE_STEPS[1];
  const pose = step === 5 ? 'happy' : step === 1 ? 'construction' : step === 2 ? 'thinking' : 'writing';

  return (
    <section
      className={`guide-panel mb-8 rounded-[1.75rem] border border-white/15 bg-[#0b0d0e]/95
        shadow-2xl backdrop-blur-xl ${collapsed ? 'is-collapsed' : ''}`}
      style={{ '--glow': color, '--beat': `${60 / bpm}s` }}
      aria-label="First Song guide"
    >
      <div className="guide-summary flex min-h-14 items-center gap-3 px-4 py-3 sm:px-5">
        <img src={`/pizzul/${pose}.png`} alt="Pizzul" className="h-11 w-11 object-contain" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black uppercase tracking-[0.16em]" style={{ color }}>
            Step {step} of 5
          </p>
          <p className="truncate font-heading text-lg font-black">{detail.title}</p>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((current) => !current)}
          className="guide-collapse flex min-h-11 min-w-11 items-center justify-center rounded-full
            text-white/65 hover:bg-white/10"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand First Song guide' : 'Collapse First Song guide'}
        >
          {collapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
        </button>
      </div>

      <div className="guide-details border-t border-white/10 px-4 pb-5 pt-4 sm:px-5">
        <div className="guide-progress grid grid-cols-5 gap-1.5" aria-label={`Step ${step} of 5`}>
          {Array.from({ length: 5 }).map((_, index) => (
            <span
              key={index}
              className="h-2 rounded-full"
              style={{ backgroundColor: index < step ? color : 'rgba(255,255,255,0.13)' }}
            />
          ))}
        </div>

        {step < 5 && (
          <div className="mt-4 grid gap-4 md:grid-cols-[5rem_minmax(0,1fr)]">
            <img src={`/pizzul/${pose}.png`} alt="" className="hidden h-20 w-20 object-contain md:block" />
            <div>
              <h2 className="font-heading text-2xl font-black">{detail.title}</h2>
              <p className="mt-2 text-base leading-relaxed text-white/75">{detail.text}</p>
              {detail.tip && <p className="mt-3 text-base font-black" style={{ color }}>{detail.tip}</p>}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <p className="text-base font-bold text-white/65">Use the sound packs above.</p>
            <button
              type="button"
              onClick={onNoSound}
              className="min-h-11 rounded-full border border-white/15 px-4 text-base font-black"
            >
              No sound right now
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="mt-4 space-y-3">
            <BeatCounter color={color} playing={playing} />
            <button
              type="button"
              onClick={onFlowConfirmed}
              className="min-h-11 rounded-full border border-[color:var(--glow)] px-4
                text-base font-black"
            >
              Got a flow
            </button>
          </div>
        )}

        {(step === 3 || step === 4) && !aiAvailable && <LocalStarters step={step} />}

        {step === 4 && (
          <p className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4 text-base
            font-bold leading-relaxed text-white/70"
          >
            Each dot is a syllable. 16 fills a bar at a normal rap pace. Under 16 is fine.
            Over 16 gets crowded.
          </p>
        )}

        {step === 5 && (
          <>
            <div className="mt-2 flex items-center gap-4">
              <img src="/pizzul/happy.png" alt="Pizzul" className="h-20 w-20 object-contain" />
              <h2 className="font-heading text-3xl font-black">You wrote a song.</h2>
            </div>
            <FinishCard
              color={color}
              onDownload={onDownload}
              onKeepWriting={onKeepWriting}
              song={song}
            />
          </>
        )}

        <div className="guide-controls mt-5 flex flex-wrap items-center gap-2 border-t
          border-white/10 pt-4"
        >
          <button
            type="button"
            onClick={onBack}
            disabled={step === 1}
            className="min-h-11 rounded-full px-4 text-base font-black text-white/65
              disabled:opacity-30"
          >
            Back
          </button>
          {step < 5 && (
            <button
              type="button"
              onClick={onNext}
              disabled={!complete}
              className="neon-btn min-h-11 rounded-full px-5 text-base font-black text-black
                disabled:opacity-35"
              style={{ backgroundColor: color }}
            >
              Next
            </button>
          )}
          {step < 5 && (
            <button
              type="button"
              onClick={onSkip}
              className="min-h-11 rounded-full px-3 text-base font-bold text-white/45
                hover:text-white/75"
            >
              Skip this step
            </button>
          )}
          <button
            type="button"
            onClick={onExit}
            className="ml-auto min-h-11 rounded-full px-3 text-base font-bold text-white/45
              hover:text-white/75"
          >
            Exit guide
          </button>
        </div>
      </div>
    </section>
  );
};

export default GuidePanel;
